import simpleRestProvider from 'ra-data-simple-rest';
import { fetchUtils } from 'ra-core';
import { DataProviderWithCustomMethods } from '../types';
import { API_URL } from '../constants';
import { authProvider } from '../authProvider';
import queryString from 'query-string';

export const fetchJson = async (
  url: string,
  options: fetchUtils.Options = {}
) => {
  const accessToken = await authProvider.getAccessToken();
  if (!accessToken) {
    return Promise.reject('No access token available');
  }

  const customHeaders = (options.headers ||
    new Headers({
      Accept: 'application/json',
    })) as Headers;

  customHeaders.set('Authorization', `Bearer ${accessToken}`);
  options.headers = customHeaders;

  let requestUrl = url;
  if (!requestUrl.startsWith('http')) {
    requestUrl = API_URL + requestUrl;
  }

  const urlObject = new URL(requestUrl);
  const advertiserIdJson = localStorage.getItem('RaStore.advertiser_id');
  if (advertiserIdJson) {
    const advertiserId = JSON.parse(advertiserIdJson);
    urlObject.searchParams.set('advertiser_id', advertiserId);
  }

  return fetchUtils.fetchJson(urlObject.toString(), options);
};

const simpleRestDataProvider: DataProviderWithCustomMethods = {
  ...simpleRestProvider(API_URL, fetchJson),

  getList: async (resource: string, params: any) => {
    const defaultSort = ['created_at', 'DESC'];
    const { page, perPage } = params.pagination;
    const { field, order } = params.sort;

    const query = {
      // It doesn't make sens to sort by ID since we use UUID, so override the sort with ['created_at', 'DESC']
      sort:
        field === 'id'
          ? JSON.stringify(defaultSort)
          : JSON.stringify([field, order]),
      range: JSON.stringify([(page - 1) * perPage, page * perPage - 1]),
      filter: JSON.stringify(params.filter),
    };
    const url = `${API_URL}/${resource}?${queryString.stringify(query)}`;
    const { json, headers } = await fetchJson(url, {
      signal: params.signal,
    });
    return {
      data: json,
      total: parseInt(
        headers.get('content-range')?.split('/').pop() || '0',
        10
      ),
    };
  },
  launchCampaign: async (campaignId: string, data: Record<string, unknown>) => {
    const { json } = await fetchJson(
      `${API_URL}/campaigns/${campaignId}/launch`,
      {
        method: 'POST',
        body: JSON.stringify(data),
      }
    );
    return json;
  },
  getBrandForLang: async (campaignId: string, lang: string) => {
    const { json } = await fetchJson(
      `${API_URL}/campaigns/${campaignId}/brand?lang=${encodeURIComponent(lang)}`
    );
    return json;
  },
  getCampaignChoices: async () => {
    const { json } = await fetchJson(`${API_URL}/campaign-choices`);
    return json;
  },
  saveCreativeDraft: async (data: Record<string, unknown>) => {
    const { json } = await fetchJson(`${API_URL}/campaign-creatives/draft`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return json;
  },
  createArticleBrief: async (data) => {
    const { json } = await fetchJson(
      `${API_URL}/campaign-creatives/article-brief`,
      { method: 'POST', body: JSON.stringify(data) }
    );
    return json;
  },
  saveDraftArticleBrief: async (data) => {
    const { json } = await fetchJson(
      `${API_URL}/campaign-creatives/article-brief/draft`,
      { method: 'POST', body: JSON.stringify(data) }
    );
    return json;
  },
  updateArticleBrief: async (id, data) => {
    const { json } = await fetchJson(
      `${API_URL}/campaign-creatives/article-brief/${id}`,
      { method: 'PUT', body: JSON.stringify(data) }
    );
    return json;
  },
  updateDraftArticleBrief: async (id, data) => {
    const { json } = await fetchJson(
      `${API_URL}/campaign-creatives/article-brief/${id}/draft`,
      { method: 'PUT', body: JSON.stringify(data) }
    );
    return json;
  },
  previewBlob: async (url, data) => {
    const accessToken = await authProvider.getAccessToken();
    if (!accessToken) {
      return Promise.reject('No access token available');
    }
    const headers = new Headers({
      'Content-Type': 'application/json',
      Accept: 'application/json',
    }) as Headers;

    headers.set('Authorization', `Bearer ${accessToken}`);

    const response = await fetch(API_URL + url, {
      method: 'POST',
      headers,
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const errorData = await response
        .json()
        .catch(() => ({ message: response.statusText }));
      throw new Error(`${errorData.message || 'Something went wrong'}`);
    }

    const blob = await response.blob();
    const blobUrl = window.URL.createObjectURL(blob);
    window.open(blobUrl);
  },
};

// Check if we're in a dev environment that supports the dev toolbar
function isDevEnvironment(): boolean {
  const env = import.meta.env.VITE_ENVIRONMENT;
  if (env === 'production') return false;
  return (
    !env ||
    env === 'development' ||
    env === 'feature-01' ||
    env === 'feature-02'
  );
}

// Get the data provider mode from localStorage (set by DevToolbar)
function getDataProviderMode(): 'fake' | 'real' {
  try {
    const stored = localStorage.getItem('dev-data-provider');
    if (stored === 'real' || stored === 'fake') {
      return stored;
    }
  } catch {
    // Ignore
  }
  return 'real'; // Default to real
}

// Check if data should be cleared (set by DevToolbar)
function isDataCleared(): boolean {
  return localStorage.getItem('dev-clear-data') === 'true';
}

export const getDataProvider = async () => {
  // Check DevToolbar settings in dev environments
  if (isDevEnvironment()) {
    const mode = getDataProviderMode();
    const cleared = isDataCleared();

    console.log(
      '[dataProvider] Dev environment detected, mode:',
      mode,
      'cleared:',
      cleared
    );

    if (mode === 'fake') {
      const { fakeDataProvider, createEmptyDataProvider } = await import(
        './fakeDataProvider'
      );
      return cleared ? createEmptyDataProvider() : fakeDataProvider;
    }

    return simpleRestDataProvider;
  }

  return simpleRestDataProvider;
};

export const getDashboardPermissions = async () => {
  const { json } = await fetchJson(`${API_URL}/dashboard-permissions`, {
    method: 'GET',
  });
  return json;
};

// Stable IDs so the rest of the app (localStorage `advertiser_id`, routing,
// data-provider filters) stays consistent across reloads in fake mode.
const FAKE_PROFILE = {
  first_name: 'Mike',
  last_name: 'G.',
  phone_number: '',
  job_title: 'Marketing Lead',
  company_name: 'Globensky Inc',
  advertisers: [
    { id: 'fake-advertiser-nike', name: 'Nike' },
    { id: 'fake-advertiser-nike-2', name: 'Nike 2' },
  ],
};

export const getUserProfile = async () => {
  // In dev + fake mode, return a mocked profile so we can demo the
  // multi-advertiser switcher without depending on the real backend.
  if (isDevEnvironment() && getDataProviderMode() === 'fake') {
    return FAKE_PROFILE;
  }
  const { json } = await fetchJson(`${API_URL}/profile`, {
    method: 'GET',
  });
  return json;
};
